import { useState, useCallback } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card } from "@app/components/shared/Card";
import { Button } from "@app/components/shared/Button";
import { Skeleton } from "@app/components/shared/Skeleton";
import {
  generateIntelligenceReport,
  listIntelligenceReports,
  getIntelligenceReportDetail,
  approveRecommendation,
  rejectRecommendation,
  applyRecommendation,
  REPORT_TYPES,
  type IntelligenceReportItem,
  type IntelligenceReportListResponse,
  type IntelligenceReportDetailResponse,
  type IntelligenceRecommendation,
  type ApproveRejectResponse,
} from "@app/services/learningIntelligenceApi";
import { logger } from "@app/services/logger";
import "./aiIntelligencePanel.css";

interface AiIntelligencePanelProps {
  isVisible: boolean;
}

const REPORT_TYPE_OPTIONS = [
  { value: "general", label: "General Analysis" },
  { value: "winner_vs_loser", label: "Winner vs Loser" },
  { value: "winner_vs_breakeven", label: "Winner vs Breakeven" },
  { value: "loser_vs_breakeven", label: "Loser vs Breakeven" },
  { value: "filter_effectiveness", label: "Filter Effectiveness" },
  { value: "weight_recommendation", label: "Weight Recommendation" },
  { value: "custom", label: "Custom Prompt" },
] as const;

const PRIORITY_CONFIG: Record<string, { className: string; label: string }> = {
  critical: { className: "priority-badge--critical", label: "Critical" },
  high: { className: "priority-badge--high", label: "High" },
  medium: { className: "priority-badge--medium", label: "Medium" },
  low: { className: "priority-badge--low", label: "Low" },
};

const STATUS_CONFIG: Record<string, { className: string; label: string }> = {
  pending: { className: "status-badge--pending", label: "Pending" },
  approved: { className: "status-badge--approved", label: "Approved" },
  rejected: { className: "status-badge--rejected", label: "Rejected" },
  applied: { className: "status-badge--applied", label: "Applied" },
};

interface IntelligenceMutations {
  generateMutation: ReturnType<typeof useMutation<any, Error, void>>;
  approveMutation: ReturnType<typeof useMutation<any, Error, string>>;
  rejectMutation: ReturnType<typeof useMutation<any, Error, {recommendationId: string; reason: string}>>;
  applyMutation: ReturnType<typeof useMutation<any, Error, string>>;
}

function useIntelligenceMutations(
  queryClient: ReturnType<typeof useQueryClient>,
  reportType: string,
  days: number,
  tradeCount: number,
  customPrompt: string,
  onReportGenerated: (reportId: string) => void,
  onGenerationFailed?: (errorMessage: string) => void,
): IntelligenceMutations {
  const generateMutation = useMutation<IntelligenceReportItem, Error, void>({
    mutationFn: () => {
      const params: Record<string, unknown> = {
        report_type: reportType,
        days,
        max_trades: tradeCount,
      };
      if (reportType === "custom" && customPrompt.trim()) {
        params.custom_prompt = customPrompt.trim();
      }
      return generateIntelligenceReport(params as { report_type: string; days?: number; max_trades?: number; custom_prompt?: string });
    },
    onSuccess: (data) => {
      if (data.status === "failed") {
        const errorMsg = data.error_message || "Report generation failed. Check AI provider configuration.";
        logger.error("Report generation failed", new Error(errorMsg));
        if (onGenerationFailed) {
          onGenerationFailed(errorMsg);
        }
        return;
      }
      logger.info("Intelligence report generated successfully", { reportId: data.id });
      onReportGenerated(data.id);
      queryClient.invalidateQueries({ queryKey: ["intelligenceReports"] });
    },
    onError: (error: Error) => {
      logger.error("Failed to generate intelligence report", error);
    },
  });

  const approveMutation = useMutation<ApproveRejectResponse, Error, string>({
    mutationFn: (recommendationId: string) => approveRecommendation(recommendationId),
    onSuccess: () => {
      logger.info("Recommendation approved");
      queryClient.invalidateQueries({ queryKey: ["intelligenceReportDetail"] });
    },
    onError: (error: Error) => {
      logger.error("Failed to approve recommendation", error);
    },
  });

  const rejectMutation = useMutation<ApproveRejectResponse, Error, {recommendationId: string; reason: string}>({
    mutationFn: ({ recommendationId, reason }: { recommendationId: string; reason: string }) =>
      rejectRecommendation(recommendationId, reason),
    onSuccess: () => {
      logger.info("Recommendation rejected");
      queryClient.invalidateQueries({ queryKey: ["intelligenceReportDetail"] });
    },
    onError: (error: Error) => {
      logger.error("Failed to reject recommendation", error);
    },
  });

  const applyMutation = useMutation<ApproveRejectResponse, Error, string>({
    mutationFn: (recommendationId: string) => applyRecommendation(recommendationId),
    onSuccess: () => {
      logger.info("Recommendation applied");
      queryClient.invalidateQueries({ queryKey: ["intelligenceReportDetail"] });
    },
    onError: (error: Error) => {
      logger.error("Failed to apply recommendation", error);
    },
  });

  return { generateMutation, approveMutation, rejectMutation, applyMutation };
}

interface IntelligenceHandlers {
  handleGenerate: () => void;
  handleViewReport: (reportId: string) => void;
  handleBackToList: () => void;
  handleApprove: (recommendationId: string) => void;
  handleReject: (recommendationId: string) => void;
  handleApply: (recommendationId: string) => void;
}

function useIntelligenceHandlers(
  mutations: IntelligenceMutations,
  onViewReport: (id: string | null) => void,
): IntelligenceHandlers {
  const handleGenerate = useCallback(() => {
    mutations.generateMutation.mutate();
  }, [mutations.generateMutation]);

  const handleViewReport = useCallback((reportId: string) => {
    onViewReport(reportId);
  }, [onViewReport]);

  const handleBackToList = useCallback(() => {
    onViewReport(null);
  }, [onViewReport]);

  const handleApprove = useCallback((recommendationId: string) => {
    mutations.approveMutation.mutate(recommendationId);
  }, [mutations.approveMutation]);

  const handleReject = useCallback((recommendationId: string) => {
    const reason = window.prompt("Please provide a reason for rejection:");
    if (reason === null) return;
    mutations.rejectMutation.mutate({ recommendationId, reason });
  }, [mutations.rejectMutation]);

  const handleApply = useCallback((recommendationId: string) => {
    const confirmed = window.confirm(
      "Are you sure you want to apply this recommendation? This will update the system configuration."
    );
    if (!confirmed) return;
    mutations.applyMutation.mutate(recommendationId);
  }, [mutations.applyMutation]);

  return {
    handleGenerate,
    handleViewReport,
    handleBackToList,
    handleApprove,
    handleReject,
    handleApply,
  };
}

function renderLoadingState(): JSX.Element {
  return (
    <Card className="ai-intelligence-panel">
      <Skeleton style={{ height: "400px" }} />
    </Card>
  );
}

function renderErrorState(error: unknown, onRetry: () => void): JSX.Element {
  return (
    <Card className="ai-intelligence-panel ai-intelligence-panel--error">
      <h3>Error Loading Intelligence Data</h3>
      <p>{error instanceof Error ? error.message : "Unknown error"}</p>
      <Button onClick={onRetry} variant="neutral">
        Retry
      </Button>
    </Card>
  );
}

export function AiIntelligencePanel({ isVisible }: AiIntelligencePanelProps): JSX.Element | null {
  const queryClient = useQueryClient();
  const [reportType, setReportType] = useState("general");
  const [days, setDays] = useState(90);
  const [tradeCount, setTradeCount] = useState(100);
  const [customPrompt, setCustomPrompt] = useState("");
  const [selectedReportId, setSelectedReportId] = useState<string | null>(null);
  const [generateError, setGenerateError] = useState<string | null>(null);

  const reportsQuery = useQuery<IntelligenceReportListResponse>({
    queryKey: ["intelligenceReports"],
    queryFn: () => listIntelligenceReports(),
    enabled: isVisible,
    staleTime: 30000,
  });

  const detailQuery = useQuery<IntelligenceReportDetailResponse>({
    queryKey: ["intelligenceReportDetail", selectedReportId],
    queryFn: () => getIntelligenceReportDetail(selectedReportId!),
    enabled: isVisible && !!selectedReportId,
    staleTime: 30000,
  });

  const mutations = useIntelligenceMutations(
    queryClient, reportType, days, tradeCount, customPrompt,
    (reportId) => setSelectedReportId(reportId),
    (errorMsg) => setGenerateError(errorMsg),
  );

  const handlers = useIntelligenceHandlers(mutations, (id) => setSelectedReportId(id));

  if (!isVisible) {
    return null;
  }

  if (reportsQuery.isLoading) {
    return renderLoadingState();
  }

  if (reportsQuery.error) {
    return renderErrorState(reportsQuery.error, () => reportsQuery.refetch());
  }

  const reportsList = reportsQuery.data?.reports ?? [];
  const selectedReport = detailQuery.data ?? null;

  return (
    <div className="ai-intelligence-panel">
      <ReportGeneratorForm
        reportType={reportType}
        days={days}
        tradeCount={tradeCount}
        customPrompt={customPrompt}
        isGenerating={mutations.generateMutation.isPending}
        onReportTypeChange={setReportType}
        onDaysChange={setDays}
        onTradeCountChange={setTradeCount}
        onCustomPromptChange={setCustomPrompt}
        onGenerate={() => { setGenerateError(null); handlers.handleGenerate(); }}
      />

      {generateError && (
        <div className="ai-intelligence-panel__error-banner">
          <div className="ai-intelligence-panel__error-banner-header">
            <span className="ai-intelligence-panel__error-banner-icon">!</span>
            <span className="ai-intelligence-panel__error-banner-title">Report Generation Failed</span>
          </div>
          <p className="ai-intelligence-panel__error-banner-message">{generateError}</p>
        </div>
      )}

      {selectedReport && selectedReportId ? (
        <ReportView
          report={selectedReport}
          onBack={handlers.handleBackToList}
          onApprove={handlers.handleApprove}
          onReject={handlers.handleReject}
          onApply={handlers.handleApply}
          isApproving={mutations.approveMutation.isPending}
          isRejecting={mutations.rejectMutation.isPending}
          isApplying={mutations.applyMutation.isPending}
          isDetailLoading={detailQuery.isLoading}
        />
      ) : null}

      <PreviousReportsList
        reports={reportsList}
        selectedReportId={selectedReportId}
        onView={handlers.handleViewReport}
      />
    </div>
  );
}

interface ReportGeneratorFormProps {
  reportType: string;
  days: number;
  tradeCount: number;
  customPrompt: string;
  isGenerating: boolean;
  onReportTypeChange: (value: string) => void;
  onDaysChange: (value: number) => void;
  onTradeCountChange: (value: number) => void;
  onCustomPromptChange: (value: string) => void;
  onGenerate: () => void;
}

function ReportGeneratorForm({
  reportType,
  days,
  tradeCount,
  customPrompt,
  isGenerating,
  onReportTypeChange,
  onDaysChange,
  onTradeCountChange,
  onCustomPromptChange,
  onGenerate,
}: ReportGeneratorFormProps): JSX.Element {
  return (
    <Card className="ai-intelligence-panel__generator" title="Generate Intelligence Report">
      <div className="ai-intelligence-panel__form">
        <div className="ai-intelligence-panel__field">
          <label className="ai-intelligence-panel__label" htmlFor="report-type">
            Report Type
          </label>
          <select
            id="report-type"
            value={reportType}
            onChange={(e) => onReportTypeChange(e.target.value)}
            className="ai-intelligence-panel__select"
          >
            {REPORT_TYPE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        <div className="ai-intelligence-panel__field">
          <label className="ai-intelligence-panel__label" htmlFor="analysis-days">
            Analysis Period (days)
          </label>
          <input
            id="analysis-days"
            type="number"
            min={7}
            max={365}
            value={days}
            onChange={(e) => onDaysChange(Math.min(365, Math.max(7, Number(e.target.value))))}
            className="ai-intelligence-panel__input"
          />
          <span className="ai-intelligence-panel__hint">Range: 7 - 365 days</span>
        </div>

        <div className="ai-intelligence-panel__field">
          <label className="ai-intelligence-panel__label" htmlFor="trade-count">
            Trade Count
          </label>
          <input
            id="trade-count"
            type="number"
            min={10}
            max={500}
            value={tradeCount}
            onChange={(e) => onTradeCountChange(Math.min(500, Math.max(10, Number(e.target.value))))}
            className="ai-intelligence-panel__input"
          />
          <span className="ai-intelligence-panel__hint">Range: 10 - 500 trades</span>
        </div>

        {reportType === "custom" && (
          <div className="ai-intelligence-panel__field ai-intelligence-panel__field--full">
            <label className="ai-intelligence-panel__label" htmlFor="custom-prompt">
              Custom Prompt
            </label>
            <textarea
              id="custom-prompt"
              value={customPrompt}
              onChange={(e) => onCustomPromptChange(e.target.value)}
              className="ai-intelligence-panel__textarea"
              rows={4}
              placeholder="Enter your custom analysis prompt..."
            />
          </div>
        )}

        <div className="ai-intelligence-panel__actions">
          <Button
            onClick={onGenerate}
            variant="primary"
            disabled={isGenerating}
          >
            {isGenerating ? "Generating..." : "Generate Report"}
          </Button>
        </div>

        {isGenerating && (
          <div className="ai-intelligence-panel__progress">
            <Skeleton style={{ height: "8px", width: "100%" }} />
            <span className="ai-intelligence-panel__progress-text">
              Analyzing trading data and generating insights...
            </span>
          </div>
        )}
      </div>
    </Card>
  );
}

interface ReportViewProps {
  report: IntelligenceReportDetailResponse;
  onBack: () => void;
  onApprove: (id: string) => void;
  onReject: (id: string) => void;
  onApply: (id: string) => void;
  isApproving: boolean;
  isRejecting: boolean;
  isApplying: boolean;
  isDetailLoading: boolean;
}

function ReportView({
  report,
  onBack,
  onApprove,
  onReject,
  onApply,
  isApproving,
  isRejecting,
  isApplying,
  isDetailLoading,
}: ReportViewProps): JSX.Element {
  return (
    <Card
      className="ai-intelligence-panel__report"
      title={report.title ?? ""}
      subtitle={
        <span>
          Type: {report.report_type} | Trades Analyzed: {report.trade_count} |{" "}
          Generated: {new Date(report.created_at).toLocaleString("en-US")}
        </span>
      }
      actions={
        <div style={{ display: "flex", gap: "8px" }}>
          <Button onClick={() => window.print()} variant="neutral" size="sm">
            Export PDF
          </Button>
          <Button onClick={onBack} variant="neutral" size="sm">
            Back to List
          </Button>
        </div>
      }
    >
      {/* Print-only header */}
      <div className="ai-intelligence-panel__print-header" style={{ display: "none" }}>
        <h1 className="ai-intelligence-panel__print-title">
          Zakyam AI Trader &mdash; Intelligence Report
        </h1>
        <div className="ai-intelligence-panel__print-meta">
          <span>{report.title ?? "Analysis Report"}</span>
          <span className="ai-intelligence-panel__print-meta-sep">|</span>
          <span>Type: {report.report_type}</span>
          <span className="ai-intelligence-panel__print-meta-sep">|</span>
          <span>Trades Analyzed: {report.trade_count}</span>
          <span className="ai-intelligence-panel__print-meta-sep">|</span>
          <span>Generated: {new Date(report.created_at).toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" })}</span>
        </div>
      </div>
      {report.summary && (
        <div className="ai-intelligence-panel__summary">
          <h4 className="ai-intelligence-panel__summary-title">Summary</h4>
          <p className="ai-intelligence-panel__summary-text">{report.summary}</p>
        </div>
      )}

      {report.sections && report.sections.length > 0 && (
        <div className="ai-intelligence-panel__sections">
          {report.sections.map((section: {heading: string; content: string; type?: string; data?: Record<string, unknown>}, index: number) => (
            <ReportSection key={index} section={section} />
          ))}
        </div>
      )}

      {report.recommendations && report.recommendations.length > 0 && (
        <div className="ai-intelligence-panel__recommendations">
          <h4 className="ai-intelligence-panel__recommendations-title">
            Recommendations ({report.recommendations.length})
          </h4>
          <div className="ai-intelligence-panel__recommendations-list">
            {report.recommendations.map((rec: IntelligenceRecommendation) => (
              <RecommendationItem
                key={rec.id}
                recommendation={rec}
                onApprove={onApprove}
                onReject={onReject}
                onApply={onApply}
                isApproving={isApproving}
                isRejecting={isRejecting}
                isApplying={isApplying}
              />
            ))}
          </div>
        </div>
      )}

      {isDetailLoading && (
        <div className="ai-intelligence-panel__detail-loading">
          <Skeleton style={{ height: "200px" }} />
        </div>
      )}
    </Card>
  );
}

interface ReportSectionProps {
  section: {
    heading: string;
    content: string;
    type?: string;
    data?: Record<string, unknown>;
  };
}

function ReportSection({ section }: ReportSectionProps): JSX.Element {
  return (
    <div className={`ai-intelligence-panel__section ai-intelligence-panel__section--${section.type ?? "text"}`}>
      <h5 className="ai-intelligence-panel__section-heading">{section.heading}</h5>
      <div className="ai-intelligence-panel__section-content">{section.content}</div>
    </div>
  );
}

interface RecommendationItemProps {
  recommendation: IntelligenceRecommendation;
  onApprove: (id: string) => void;
  onReject: (id: string) => void;
  onApply: (id: string) => void;
  isApproving: boolean;
  isRejecting: boolean;
  isApplying: boolean;
}

function RecommendationItem({
  recommendation,
  onApprove,
  onReject,
  onApply,
  isApproving,
  isRejecting,
  isApplying,
}: RecommendationItemProps): JSX.Element {
  const priorityConf = PRIORITY_CONFIG[recommendation.priority] ?? {
    className: "priority-badge--low",
    label: recommendation.priority,
  };
  const statusConf = STATUS_CONFIG[recommendation.status] ?? {
    className: "status-badge--pending",
    label: recommendation.status,
  };
  const confidencePct = (recommendation.confidence * 100).toFixed(0);

  return (
    <div className="ai-intelligence-panel__recommendation">
      <div className="ai-intelligence-panel__rec-header">
        <div className="ai-intelligence-panel__rec-info">
          <strong className="ai-intelligence-panel__rec-label">
            {recommendation.target_label}
          </strong>
          <code className="ai-intelligence-panel__rec-key">
            {recommendation.target_setting_key}
          </code>
        </div>
        <div className="ai-intelligence-panel__rec-badges">
          <span className={`priority-badge ${priorityConf.className}`}>
            {priorityConf.label}
          </span>
          <span className={`status-badge ${statusConf.className}`}>
            {statusConf.label}
          </span>
        </div>
      </div>

      <div className="ai-intelligence-panel__rec-values">
        <div className="ai-intelligence-panel__rec-value current">
          <span className="ai-intelligence-panel__rec-value-label">Current</span>
          <span className="ai-intelligence-panel__rec-value-number">
            {recommendation.current_value}
          </span>
        </div>
        <div className="ai-intelligence-panel__rec-arrow">&rarr;</div>
        <div className="ai-intelligence-panel__rec-value recommended">
          <span className="ai-intelligence-panel__rec-value-label">Recommended</span>
          <span className="ai-intelligence-panel__rec-value-number">
            {recommendation.recommended_value}
          </span>
        </div>
        <div className="ai-intelligence-panel__rec-confidence">
          <span className="ai-intelligence-panel__rec-confidence-value">
            {confidencePct}%
          </span>
          <span className="ai-intelligence-panel__rec-confidence-label">Confidence</span>
        </div>
      </div>

      <p className="ai-intelligence-panel__rec-reason">{recommendation.reason}</p>

      {recommendation.impact_analysis && (
        <p className="ai-intelligence-panel__rec-impact">
          <strong>Impact:</strong> {recommendation.impact_analysis}
        </p>
      )}

      <div className="ai-intelligence-panel__rec-actions">
        {recommendation.status === "pending" && (
          <>
            <Button
              onClick={() => onApprove(recommendation.id)}
              variant="primary"
              size="sm"
              disabled={isApproving}
            >
              {isApproving ? "Approving..." : "Approve"}
            </Button>
            <Button
              onClick={() => onReject(recommendation.id)}
              variant="danger"
              size="sm"
              disabled={isRejecting}
            >
              {isRejecting ? "Rejecting..." : "Reject"}
            </Button>
          </>
        )}
        {recommendation.status === "approved" && (
          <Button
            onClick={() => onApply(recommendation.id)}
            variant="accent"
            size="sm"
            disabled={isApplying}
          >
            {isApplying ? "Applying..." : "Apply"}
          </Button>
        )}
      </div>
    </div>
  );
}

interface PreviousReportsListProps {
  reports: IntelligenceReportItem[];
  selectedReportId: string | null;
  onView: (id: string) => void;
}

function PreviousReportsList({
  reports,
  selectedReportId,
  onView,
}: PreviousReportsListProps): JSX.Element {
  if (reports.length === 0) {
    return (
      <Card className="ai-intelligence-panel__history ai-intelligence-panel__history--empty">
        <h4>Previous Reports</h4>
        <p>No reports have been generated yet. Use the form above to create one.</p>
      </Card>
    );
  }

  return (
    <Card className="ai-intelligence-panel__history" title="Previous Reports">
      <div className="ai-intelligence-panel__table-wrapper">
        <table className="ai-intelligence-panel__table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Status</th>
              <th>Title</th>
              <th>Trades</th>
              <th>Created</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {reports.map((report: IntelligenceReportItem) => (
              <tr
                key={report.id}
                className={
                  selectedReportId === report.id
                    ? "ai-intelligence-panel__table-row--active"
                    : ""
                }
              >
                <td>
                  <span className="ai-intelligence-panel__report-type">
                    {report.report_type}
                  </span>
                </td>
                <td>
                  <span className={`status-badge ${STATUS_CONFIG[report.status]?.className ?? "status-badge--pending"}`}>
                    {STATUS_CONFIG[report.status]?.label ?? report.status}
                  </span>
                </td>
                <td className="ai-intelligence-panel__table-title">{report.title}</td>
                <td>{report.trade_count}</td>
                <td>{new Date(report.created_at).toLocaleDateString("en-US")}</td>
                <td>
                  <Button
                    onClick={() => onView(report.id)}
                    variant="neutral"
                    size="sm"
                  >
                    View
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}
